/**
 * Frozen-group (read-only) copy + a11y wiring, in one place.
 *
 * Downgrade safety, WP-B (`.plan/features/2026-08-12-downgrade-safety-enforcement.md`):
 * when a group's OWNER is over their owned-groups cap, their newest groups go
 * read-only — nothing is deleted, no member is dropped, every read still works,
 * and every headroom-consuming write (invite / approve a request / share into /
 * rename / re-describe or publish / assign a moderator) refuses server-side with
 * `FORBIDDEN` + `data.code = "group_read_only"`.
 *
 * Copy rules (branding.md §5, §5.2.1, §8):
 *   • ONE finished sentence, present tense, no exclamation.
 *   • It names WHAT is true, never WHOSE plan made it true. A member must not
 *     learn another user's commercial state from a group surface — and the
 *     owner's own remedy lives in Settings → Plan, not in someone else's group.
 *   • Byte-identical to the server's refusal message
 *     (`apps/api/src/routes/groups/shared.ts` GROUP_READ_ONLY_MESSAGE) and to
 *     the classifier's `group_read_only` copy
 *     (`packages/client-core/src/errors/classify.ts`), so the state line, the
 *     disabled-control tooltip and a race-path toast all read the same.
 */

/** The one sentence every frozen-group surface shows. */
export const GROUP_READ_ONLY_NOTE = "This group is read-only.";

/**
 * Short trailing marker for a group ROW in a list (Library / Groups index /
 * share picker). Text, never an icon or a filled badge (design.md §2 — hairlines
 * and quiet type carry state, decoration does not).
 */
export const GROUP_READ_ONLY_MARKER = "Read-only";

/**
 * DOM id of the group page's state line. Disabled controls point at it with
 * `aria-describedby`, so the reason is announced with the control even though a
 * `title` tooltip is unreliable on a disabled button (Chromium suppresses
 * pointer events on disabled form controls) and absent on touch entirely.
 */
export const GROUP_READ_ONLY_NOTE_ID = "proof-group-read-only-note";

/**
 * Props every disabled-because-frozen control spreads, so the reason is reachable
 * three ways: the always-visible state line, the accessible description, and the
 * `title` where the platform honours it.
 *
 * Returns an empty object when the group is writable, so a spread is a no-op on
 * the normal path (no stray `title` on a live button).
 */
export function readOnlyControlProps(frozen: boolean): {
  title?: string;
  "aria-describedby"?: string;
} {
  if (!frozen) return {};
  return { title: GROUP_READ_ONLY_NOTE, "aria-describedby": GROUP_READ_ONLY_NOTE_ID };
}
